import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
    List,
    TextField,
    Edit,
    Create,
    SimpleForm,
    FunctionField,
    SelectInput,
    TextInput,
    required,
    CreateButton,
    FilterButton,
    TopToolbar,
    Show,
    TabbedShowLayout,
    ShowButton,
    useRecordContext,
    Datagrid,
    useGetList,
    PasswordInput,
    DateField,
    SearchInput,
    EditButton
} from 'react-admin';

import IconButton from '@mui/material/IconButton';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import NoteIcon from '@mui/icons-material/Note';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Typography from "@material-ui/core/Typography";
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import {
    InputAdornment, FormControl, InputLabel, MenuItem, Select,
    FormControlLabel, Switch, TextField as MuiTextField
} from '@mui/material';
import { useListContext } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { motion } from 'framer-motion';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { RowForm } from "@react-admin/ra-editable-datagrid";
import { iconStyle } from '../shared/Styles/iconsStyles';
import { showButtonCss } from '../shared/Styles/buttonStyles';
import { fieldTabForShow } from '../shared/Styles/fieldsStyles';
import { createButtonCss, filterButtonCss } from '../shared/Styles/buttonStyles';
import { listCss } from '../shared/Styles/listStyles';
import { ConfirmDialogDeleteButtonCustom } from "../shared/DeleteButtonCustom";
import GetUltimoInoltro from "./GetUltimoInoltro";
import DialogDataScadenza from "./DialogDataScadenza";
import LinearProgress from '@mui/material/LinearProgress';
import config from "../../config/config.json";

const host = config.production.host;

// Removed ListActions in favor of custom header
// const ListActions = () => { ... };


// Mappa colori per i ruoli
const ROLE_COLORS = {
    admin: '#e85a2c',
    user: '#2196f3',
    guest: '#9e9e9e',
    moderator: '#ff9800',
    editor: '#4caf50',
    candidato: '#9c27b0',
    collaboratore: '#00bcd4'
};

const ROLE_LABELS = {
    admin: 'Amministratore',
    user: 'Utente',
    guest: 'Ospite',
    moderator: 'Moderatore',
    editor: 'Editor',
    candidato: 'Candidato',
    collaboratore: 'Collaboratore'
};

// Funzione per ottenere le iniziali
const getInitials = (nome, cognome) => {
    const n = nome ? nome.charAt(0).toUpperCase() : '';
    const c = cognome ? cognome.charAt(0).toUpperCase() : '';
    return `${n}${c}` || '?';
};



// Helper per retry
const fetchWithRetry = async (url, options, retries = 3, backoff = 300) => {
    try {
        return await axios.get(url, options);
    } catch (err) {
        if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, backoff));
            return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw err;
    }
};

// Componente per visualizzare i gruppi dell'utente
const UserGroupsChips = ({ userId }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Random delay tra 0 e 1000ms per evitare picchi di richieste
        const delay = Math.floor(Math.random() * 1000);
        const timer = setTimeout(() => {
            loadUserGroups();
        }, delay);
        return () => clearTimeout(timer);
    }, [userId]);

    const loadUserGroups = async () => {
        if (!userId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const jwt = localStorage.getItem('feathers-jwt');

            // Usa fetchWithRetry per gestire errori transitori
            const response = await fetchWithRetry(`${host}/proc-get-users-gruppi`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`
                },
                params: {
                    p_input_usersId: userId
                }
            });

            const groupsData = response.data?.data || [];
            setGroups(groupsData);
            setError(null);
        } catch (err) {
            console.error('Errore caricamento gruppi:', err);
            setError(err.message);
            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" sx={{ color: '#999' }}>Caricamento...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Tooltip title={`Errore: ${error}`}>
                <Chip
                    label="Errore"
                    size="small"
                    color="error"
                    icon={<CancelIcon />}
                    variant="outlined"
                />
            </Tooltip>
        );
    }

    if (!groups || groups.length === 0) {
        return (
            <Chip
                label="Nessun gruppo"
                size="small"
                variant="outlined"
                sx={{ color: '#999', borderColor: '#ddd' }}
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: '300px' }}>
            {groups.slice(0, 3).map((group, index) => (
                <Tooltip
                    key={index}
                    title={
                        <Box>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {group.nome || group.nome_gruppo}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                <strong>Permessi:</strong>
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                • Lettura: {group.read_permission ? '✓' : '✗'}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                • Creazione: {group.create_permission ? '✓' : '✗'}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                • Modifica: {group.update_permission ? '✓' : '✗'}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                • Eliminazione: {group.remove_permission ? '✓' : '✗'}
                            </Typography>
                        </Box>
                    }
                    arrow
                >
                    <Chip
                        icon={<GroupIcon />}
                        label={group.nome || group.nome_gruppo || `Gruppo ${group.tbl_gruppoId}`}
                        size="small"
                        sx={{
                            backgroundColor: group.flAttivo ? '#4caf50' : '#9e9e9e',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            '& .MuiChip-icon': {
                                color: '#fff',
                                fontSize: '0.9rem'
                            }
                        }}
                    />
                </Tooltip>
            ))}
            {groups.length > 3 && (
                <Chip
                    label={`+${groups.length - 3}`}
                    size="small"
                    variant="outlined"
                    sx={{
                        fontSize: '0.7rem',
                        height: '20px',
                        color: '#666',
                        borderColor: '#ddd'
                    }}
                />
            )}
        </Box>
    );
};

// Validazione
const validateOperatoreCreation = (values) => {
    const errors = {};
    if (!values.nome) {
        errors.nome = 'Il nome è necessario';
    }
    if (!values.cognome) {
        errors.cognome = 'Il cognome è necessario';
    }
    return errors
};


// Componente separato per le azioni
const ActionsCell = ({ record }) => {
    const navigate = useNavigate();

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Visualizza dettagli">
                <IconButton
                    size="small"
                    onClick={() => navigate(`/users/${record.id}`)}
                    sx={{
                        color: '#4A90E2',
                        '&:hover': { bgcolor: 'rgba(74, 144, 226, 0.1)' }
                    }}
                >
                    <PersonIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Modifica utente">
                <IconButton
                    size="small"
                    onClick={() => navigate(`/users/${record.id}/edit`)}
                    sx={{
                        color: '#ff9800',
                        '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' }
                    }}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const UsersFilterCustom = () => {
    const { filterValues, setFilters, refetch } = useListContext();
    const navigate = useNavigate();
    const [emailSearch, setEmailSearch] = useState(filterValues['email[$like]']?.replace(/%/g, '') || '');
    const [idSearch, setIdSearch] = useState(filterValues.id || '');

    useEffect(() => {
        setEmailSearch(filterValues['email[$like]']?.replace(/%/g, '') || '');
    }, [filterValues['email[$like]']]);

    useEffect(() => {
        setIdSearch(filterValues.id || '');
    }, [filterValues.id]);

    const handleSearch = () => {
        const newFilters = { ...filterValues };

        // Email filter with LIKE pattern - use bracket notation for Sequelize
        if (emailSearch.trim()) {
            newFilters['email[$like]'] = `%${emailSearch.trim()}%`;
        } else {
            delete newFilters['email[$like]'];
        }

        // ID filter (exact match)
        if (idSearch.trim()) {
            newFilters.id = idSearch.trim();
        } else {
            delete newFilters.id;
        }

        setFilters(newFilters, null);
    };

    const handleClearFilters = () => {
        setEmailSearch('');
        setIdSearch('');
        setFilters({ status: filterValues.status }, null); // Keep only status filter
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };




    const handleStatusChange = (event) => {
        const value = event.target.value;
        setFilters({ ...filterValues, status: value === 'all' ? undefined : (value === 'active' ? true : false) }, null);
    };

    return (
        <Box sx={{ mb: 3, mt: 2 }}>
            {/* Header Card */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                                    <GroupIcon sx={{ fontSize: 32, color: 'white' }} />
                                </Avatar>
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold', marginBottom: '8px' }}>
                                    Gestione Utenti
                                </Typography>
                                <Typography variant="body1" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Visualizza e gestisci gli utenti della piattaforma
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Crea nuovo utente">
                                        <IconButton
                                            onClick={() => navigate('/users/create')}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            <AddCircleIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Aggiorna">
                                        <IconButton
                                            onClick={() => refetch()}
                                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Filters Card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
            >
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <MuiTextField
                                    fullWidth
                                    placeholder="Cerca per email..."
                                    value={emailSearch}
                                    onChange={(e) => setEmailSearch(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: emailSearch && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setEmailSearch('')} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <MuiTextField
                                    fullWidth
                                    placeholder="Cerca per ID..."
                                    value={idSearch}
                                    onChange={(e) => setIdSearch(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: idSearch && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setIdSearch('')} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSearch}
                                    sx={{ height: '56px' }}
                                >
                                    Cerca
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleClearFilters}
                                    sx={{ height: '56px' }}
                                >
                                    Azzera filtri
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Stato</InputLabel>
                                    <Select
                                        value={filterValues.status === undefined ? 'all' : (filterValues.status ? 'active' : 'inactive')}
                                        onChange={handleStatusChange}
                                        label="Stato"

                                    >
                                        <MenuItem value="all">Tutti</MenuItem>
                                        <MenuItem value="active">Attivi</MenuItem>
                                        <MenuItem value="inactive">Disattivi</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </motion.div>
        </Box >
    );
};

export const UsersList = props => {
    const navigate = useNavigate();

    return (
        <List perPage={25} actions={null} exporter={false} sort={{ field: 'id', order: 'DESC' }} {...props}>
            <UsersFilterCustom />
            <Datagrid
                sx={{
                    ...listCss,
                    '& .RaDatagrid-headerCell': {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        color: '#e85a2c'
                    },
                    '& .RaDatagrid-rowCell': {
                        padding: '12px',
                    }
                }}
                bulkActionButtons={false}
                rowClick={(id) => `/users/${id}`}
            >
                {/* ID Utente */}
                <TextField
                    label="ID"
                    source="id"
                    sortable={true}
                />

                {/* Utente con Avatar */}
                <FunctionField
                    label="Utente"
                    sortable={true}
                    sortBy="email"
                    render={record => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                sx={{
                                    bgcolor: ROLE_COLORS[record.roles] || '#757575',
                                    width: 36,
                                    height: 36,
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {getInitials(record.nome, record.cognome)}
                            </Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                                    {record.nome || '-'} {record.cognome || '-'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                    <EmailIcon sx={{ color: '#999', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                        {record.email}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                />

                {/* Ruolo con Chip */}
                <FunctionField
                    label="Ruolo"
                    sortable={true}
                    sortBy="roles"
                    render={record => (
                        <Chip
                            icon={<PersonIcon />}
                            label={ROLE_LABELS[record.roles] || record.roles}
                            size="small"
                            sx={{
                                backgroundColor: ROLE_COLORS[record.roles] || '#757575',
                                color: '#fff',
                                fontWeight: 'bold',
                                '& .MuiChip-icon': {
                                    color: '#fff'
                                }
                            }}
                        />
                    )}
                />



                {/* Gruppi */}
                <FunctionField
                    label="Gruppi"
                    render={record => <UserGroupsChips userId={record.id} />}
                />

                {/* Status con Badge */}
                <FunctionField
                    label="Stato"
                    sortable={true}
                    sortBy="status"
                    render={record => (
                        record.status !== null && record.status !== undefined ? (
                            <Chip
                                icon={record.status ? <CheckCircleIcon /> : <CancelIcon />}
                                label={record.status ? 'Attivo' : 'Disattivo'}
                                size="small"
                                color={record.status ? 'success' : 'default'}
                                variant={record.status ? 'filled' : 'outlined'}
                            />
                        ) : (
                            <Chip
                                label="N/A"
                                size="small"
                                variant="outlined"
                                sx={{ color: '#999', borderColor: '#ddd' }}
                            />
                        )
                    )}
                />

                {/* Data Creazione */}
                <DateField
                    label="Creato il"
                    source="createdAt"
                    showTime
                    options={{
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    }}
                />

                {/* Azioni */}
                <FunctionField
                    label="Azioni"
                    render={record => <ActionsCell record={record} />}
                />
            </Datagrid>
        </List>
    );
}

const UsersListForm = (props) => (
    <RowForm {...props}>
        {<TextField label="User Id" source="id" />}
        {<TextInput label="Email" source="email" validate={[required()]} />}
        {<TextInput label="Nome" source="nome" validate={[required()]} />}
        {<TextInput label="Cognome" source="cognome" validate={[required()]} />}
        {<SelectInput
            label="Ruolo"
            source="roles"
            validate={[required()]}
            choices={[
                { id: 'admin', name: 'Amministratore' },
                { id: 'user', name: 'Utente' },
                { id: 'guest', name: 'Ospite' },
                { id: 'moderator', name: 'Moderatore' },
                { id: 'editor', name: 'Editor' },
                { id: 'candidato', name: 'Candidato' },
                { id: 'collaboratore', name: 'Collaboratore' }
            ]}
        />}
        {<PasswordInput label="Password" source="password" />}
    </RowForm>
);

// const UsersFilters = [ ... ];

export const UsersCreate = (props) => {

    /*     const [create] = useCreate();
        const usersCreate = (data) => {
            create('users', { data });
        }; */

    return (
        <Create title={<>
            <Button>
                &nbsp;Crea Nuovo User
            </Button>
        </>} {...props}>
            <SimpleForm redirect="show">
                {<TextInput label="Email" source="email" validate={[required()]} />}
                {<PasswordInput label="Passsword" source="password" validate={[required()]} />}
                {<SelectInput label="Ruolo" source="roles" validate={[required()]} choices={[
                    { id: 'admin', name: 'admin' },
                    { id: 'candidato', name: 'candidato' },
                    { id: 'collaboratore', name: 'collaboratore' }]} />}
            </SimpleForm>
        </Create>
    );
}

/*  */

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    props.value,
                )}%`}</Typography>
            </Box>
        </Box>
    );
}

const PercentualeAvanzamento = ({ tblQuizId }) => {
    console.log(tblQuizId);
    const { data, loading, error } = useGetList('view-quiz-avanzamento', {
        filter: { tblQuizId: tblQuizId }
    });

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    // Verifica se data è definito e se ha almeno un elemento
    if (!data || data.length === 0) {
        return <p>No data available</p>;
    }

    return (
        <>
            <Typography variant="subtitle1" component="div">
                <strong>Percentuale Avanzamento </strong>
            </Typography>
            <LinearProgressWithLabel value={data[0].percent_avanzamento} />
        </>
    );
};

const GestioneTblQuizInShow = ({ id, ...props }) => {

    const record = useRecordContext();
    const { data, total, isLoading } = useGetList(
        'tbl-quiz',
        { filter: { usersId: record.id } }
    );
    if (!record) return null;


    return (<>{(total === 0)
        ? (
            (record.roles !== "candidato")
                ?
                <Card variant="outlined" sx={{
                    m: 8,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: '#fff7e6', // Light orange background
                    '&:hover': {
                        boxShadow: 6,
                        backgroundColor: '#ffe4b3' // Darker orange on hover
                    }
                }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" component="div">
                                    <strong>Attenzione. Non sono previsti Quiz per user con ruolo admin e collaboratore.</strong>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
                : <Card variant="outlined" sx={{
                    m: 8,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: '#fff7e6', // Light orange background
                    '&:hover': {
                        boxShadow: 6,
                        backgroundColor: '#ffe4b3' // Darker orange on hover
                    }
                }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" component="div">
                                    <strong>Lista vuota vuota.</strong>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
        )

        : <Datagrid
            data={data}
            total={total}
            isLoading={isLoading}
            bulkActionButtons={false}
        >
            <Card variant="outlined" sx={{
                m: 1,
                borderRadius: 2,
                boxShadow: 3,
                backgroundColor: '#fff7e6', // Light orange background
                '&:hover': {
                    boxShadow: 6,
                    backgroundColor: '#ffe4b3' // Darker orange on hover
                }
            }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" component="div">
                                <strong>Quiz Id:</strong> <TextField source="id"></TextField>
                            </Typography>
                            <Typography variant="subtitle1" component="div">
                                <strong>Data Creazione:</strong> <DateField source="createdAt" showTime options={{ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }}></DateField>
                            </Typography>
                            <Grid container alignItems="center">
                                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" component="div">
                                        <strong>Data Scadenza:</strong> <DateField source="dataScadenza" showTime options={{ year: 'numeric', month: '2-digit', day: '2-digit' }} />
                                    </Typography>
                                </Grid>

                                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ marginRight: 1, transform: 'scale(0.8)' }}>
                                        <DialogDataScadenza />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Typography variant="subtitle1" component="div">
                                <strong>Descrizione:</strong> <TextField source="descrizione" variant="outlined" size="small" />
                            </Typography>
                            <Typography variant="subtitle1" component="div">
                                <strong>Stato:</strong> <TextField source="stato" variant="outlined" size="small" />
                            </Typography>
                            <FunctionField
                                source="id"
                                render={record => <PercentualeAvanzamento tblQuizId={record["id"]}></PercentualeAvanzamento>}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ backgroundColor: '#f0f0f0', padding: '10px', borderRadius: '4px', marginTop: 2 }}>
                        <FunctionField
                            source="id"
                            label=""
                            render={record => <><GetUltimoInoltro tblQuizId={record.id}></GetUltimoInoltro></>}
                        />
                    </Box>
                    <hr></hr>

                    <Box>
                        <IconButton aria-label="delete" sx={{ color: '#e85a2c' }}>
                            <ConfirmDialogDeleteButtonCustom
                                titolo={"Cancella Quiz"}
                                messaggio={"Sei sicuro di voler cancellare questo Quiz ?"}
                                servizio={"tbl-quiz"} />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card >

        </Datagrid >}</>);

}

export const UsersShow = (props) => {



    return (
        <Show title={<>
            <Button>
                &nbsp;Dettaglio User
            </Button>
            <FunctionField source="email" render={record => record && `${record.email}`} />
        </>} actions={null}>

            <Grid item xs={12}>
                <Card sx={{ minWidth: 275 }}>
                    <CardContent>
                        <Typography sx={{ mb: 1.5, color: 'text.secondary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#e85a2c' }}>
                                <NoteIcon sx={iconStyle} />
                                Email : <FunctionField source="email" render={record => record && `${record.email}`} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#e85a2c' }}>
                                &nbsp;<h2><strong><FunctionField source="roles" render={record => record && `Ruolo: ${record.roles}`} /></strong></h2>
                            </Box>
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <TabbedShowLayout>
                <TabbedShowLayout.Tab label="Dati User" sx={fieldTabForShow} icon={<NoteIcon sx={iconStyle} />}>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={12}>
                            <Card sx={{ minWidth: 275 }}>
                                <CardContent>
                                    <Typography sx={{ mb: 1.5, color: 'text.secondary' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                            <NoteIcon />
                                            ID USER: <TextField label="Id User" source="id" />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                            <NoteIcon />
                                            Ruolo: <TextField label="Ruolo" source="roles" />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                            <EmailIcon />
                                            Email: <TextField label="Email" source="email" />
                                        </Box>

                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </TabbedShowLayout.Tab>
            </TabbedShowLayout>
        </Show>
    );
}